import * as React from 'react';
import { CircularProgress, Typography } from '@mui/material';
import Avatar, { type AvatarProps } from '@mui/material/Avatar';
import { styled } from '@mui/material/styles';

const buildInitials = (name = '') => {
  const names = name.split(' ');
  const firstName = names[0];
  const lastName = names.length > 1 ? names[names.length - 1] : ' ';

  return `${firstName?.[0]}${lastName?.[0]}`;
};

export interface UserAvatarProps extends Omit<AvatarProps, 'variant'> {
  imageSource?: string;
  size?: number;
  name: string;
  variant?: 'navbar' | 'menu';
  loading?: boolean;
}

const StyledAvatar = styled(Avatar)<AvatarProps>(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
  container: 'avatar / size',
  textTransform: 'uppercase',

  '@container avatar (min-width: 40px)': {
    '& .initials': {
      fontSize: '16px',
    },
  },

  '@container avatar (min-width: 64px)': {
    '& .initials': {
      fontSize: '24px',
    },
  },
}));

const UserAvatarComponent = ({
  imageSource,
  size: customSize,
  name,
  variant = 'navbar',
  loading,
  ...props
}: UserAvatarProps) => {
  const variants = {
    navbar: { size: '40px' },
    menu: { size: '40px' },
  };

  const { size } = variants[variant] || {};

  return (
    <StyledAvatar
      alt={name}
      data-testid="user-avatar"
      src={!loading ? imageSource : ''}
      sx={{ height: customSize ?? size, width: customSize ?? size }}
      {...props}
    >
      {loading ? (
        <CircularProgress color="inherit" />
      ) : (
        <Typography
          className="initials"
          fontFamily="Carbona"
          color="white"
          aria-label={name}
        >
          {name && buildInitials(name)}
        </Typography>
      )}
    </StyledAvatar>
  );
};

export const UserAvatar = React.memo(UserAvatarComponent);
